import { ListBaseModel } from '@/api/models';
import { AttachmentsModel } from '@/features/attachment/_models/attachment';
import { TagTargetBaseModel } from '@/features/tag/models';
import { HierarchyLabelModel } from '@/features/tenant-manager/hierarchy-label/_models/hierarchy-label';
import { OrganizationBaseModel } from '@/features/tenant-manager/organization/_models/organization';
import {
  UserModel,
  UsersModel,
} from '@/features/tenant-manager/user/_models/user';
import { t } from '@/i18n';
import {
  BackendBaseModel,
  DayjsSchema,
  coerceNullToUndefined,
  paginationSearchParamSchema,
} from '@/utils';
import { z } from 'zod';
import { WorkflowApprovalModel } from './approval';
import { WorkflowSearchFormSchema } from './search';

/// ////////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////////
export const workflowType = z.enum([
  'strict', // 順番型
  'open', // 順不同型
]);
export const WorkflowTypeLabelModifier = {
  strict: t('workflow-type-strict'),
  open: t('workflow-type-open'),
} as const satisfies Record<z.infer<typeof workflowType>, string>;

export const workflowStatusType = z.enum([
  'pending',
  'reviewing',
  'approved',
  'rejected',
]);

// approve以外同じなのはおそらく業務要件
export const WorkflowStatusModifier = {
  pending: t('workflow.status-unapproved'),
  reviewing: t('workflow.status-unapproved'),
  rejected: t('workflow.status-unapproved'),
  approved: t('workflow.status-approved'),
} as const satisfies Record<z.infer<typeof workflowStatusType>, string>;

export const workflowKeyType = z.enum(['project']);

export const workflowStageType = z.enum([
  'one', // OR承認
  'all', // AND承認
]);
export const WorkflowStageTypeLabelModifier = {
  one: t('stage-type-one'),
  all: t('stage-type-all'),
} as const satisfies Record<z.infer<typeof workflowStageType>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

export const WorkflowStageModel = z.object({
  id: z.string().uuid(),
  title: z.string().nullish(),
  labelId: z.string().uuid().nullish(),
  workflowId: z.string().uuid(),
  stageType: workflowStageType,
  previousStageId: z.string().uuid().nullable(),
  approvals: WorkflowApprovalModel.array().nullish(),
  label: HierarchyLabelModel.nullable(),
  status: workflowStatusType,
});

export const WorkflowStagesModel = WorkflowStageModel.array();

export const WorkflowModel = BackendBaseModel.extend({
  workflowType,
  status: workflowStatusType,
  actionRequired: z.boolean(),
  title: z.string().min(1),
  content: z.string().nullish(),
  remark: z.string().nullish(),
  key: workflowKeyType,
  objectId: z.string().uuid(),
  objectName: z.string(),
  deadline: z.string().date().nullable(),
  tagTargets: TagTargetBaseModel.array().nullable(),
  attachments: AttachmentsModel.optional(),
  author: UserModel,
  authorId: z.string().uuid(),
  assignees: UsersModel,
  assigneeIds: z.string().uuid().array().min(1),
  sharedOrganizations: OrganizationBaseModel.pick({
    id: true,
    name: true,
  }).array(),
  sharedOrganizationIds: z.string().uuid().array(),
  stages: WorkflowStagesModel,
});

const WorkflowsModel = WorkflowModel.array();

export const WorkflowListModel = ListBaseModel.extend({
  results: WorkflowsModel,
});

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const WorkflowListParamsSchema = WorkflowSearchFormSchema.pick({
  keyword: true,
  stageLabelId: true,
  workflowStatus: true,
  actionRequired: true,
  tagIds: true,
})
  .merge(paginationSearchParamSchema)
  .extend({
    objectId: z.string().uuid(),
    createdAtTo: z.string().nullable(),
    createdAtFrom: z.string().nullable(),
    all: z.boolean(),
  })
  .partial();

export const WorkflowFormSchema = WorkflowModel.pick({
  title: true,
  content: true,
  assigneeIds: true,
  remark: true,
}).extend({
  deadline: DayjsSchema().optional(),
  tagIds: z.string().uuid().array().optional(),
  attachments: AttachmentsModel.optional(),
  sharedOrganizationRecursiveIds: z.array(
    OrganizationBaseModel.shape.id.array(),
  ),
});

export const WorkflowStageFormStageFieldSchema = z.object({
  id: z.string().uuid().nullish().transform(coerceNullToUndefined),
  title: z.string().nullish(),
  labelId: WorkflowStageModel.shape.labelId.nullish(),
  stageType: WorkflowStageModel.shape.stageType,
  approverIds: z
    .string()
    .uuid()
    .array()
    .nullish()
    .transform(coerceNullToUndefined),
});

const WorkflowStageFormStagesFieldSchema =
  WorkflowStageFormStageFieldSchema.array().nullish();

export const WorkflowStageFormSchema = z.object({
  workflowType: z.enum(['strict', 'open']),
  stages: WorkflowStageFormStagesFieldSchema,
});

export const WorkflowFromTemplateFormSchema = WorkflowFormSchema.pick({
  title: true,
  content: true,
  assigneeIds: true,
  remark: true,
  deadline: true,
  tagIds: true,
  sharedOrganizationRecursiveIds: true,
}).extend({
  workflowType: workflowType.nullish().transform(coerceNullToUndefined),
  stages: WorkflowStageFormStagesFieldSchema,
});

export const CreateWorkflowParamsSchema = WorkflowFormSchema.omit({
  sharedOrganizationRecursiveIds: true,
}).extend({
  key: workflowKeyType,
  objectId: z.string().uuid(),
  attachmentIds: z.string().array().optional(),
  sharedOrganizationIds: z.array(OrganizationBaseModel.shape.id),
  workflowType: workflowType.nullish().transform(coerceNullToUndefined),
  stages: WorkflowStageFormStagesFieldSchema,
});

export const UpdateWorkflowParamsSchema = WorkflowFormSchema.omit({
  sharedOrganizationRecursiveIds: true,
}).extend({
  id: z.string().uuid(),
  attachmentIds: z.string().array().optional(),
  sharedOrganizationIds: z.array(OrganizationBaseModel.shape.id),
});

export const BulkUpsertAndDeleteStagesSchema = z.object({
  id: z.string().uuid(),
  workflowType: workflowType.nullish().transform(coerceNullToUndefined),
  stages: WorkflowStageFormStagesFieldSchema,
});
